import { useCallback, useEffect, useState } from 'react';
import { useInfiniteQuery, useQueryClient } from 'react-query';
import { deepClone } from '../../../common/other';
import { RQPostCommentKey } from '../../../constant/QueryKeyName';
import { IPostEachCommentResponse } from '../../../interfaces/post.interface';
import {
  deletePostComment,
  getEachPostListComments,
  postNewCommentToPost,
  putPostPinComment,
  putUpdatePostComment,
  putVotePostComment,
} from '../../../apis/tms/post.api';
import {
  IUseCommentHook,
  TCommentAdd,
  TCommentDelete,
  TCommentPinTwoWays,
  TCommentUpdate,
  TCommentVote,
} from '../../../interfaces/hook.interface';

const useGetPostComments = (postId: string) => {
  return useInfiniteQuery(
    [RQPostCommentKey.PostCommentList, postId],
    getEachPostListComments,
    {
      getNextPageParam: (_lastPage: any, _allPages: any[]) => {
        return _lastPage?.hasNextPage ? _lastPage?.currentPage + 1 : undefined;
      },
      select: (data): any => {
        const lastPages = data.pages.length - 1;
        return {
          data: data.pages[lastPages].data,
          meta: {
            totalCount: data.pages[lastPages]?.totalCount || 0,
            hasNextPage: data.pages[lastPages]?.hasNextPage || false,
            pageSize: data.pages[lastPages]?.pageSize,
            currentPage: data.pages[lastPages]?.currentPage,
          },
          userRole: data.pages[lastPages]?.userRole,
        };
      },
      enabled: !!postId,
      retry: 3,
      refetchOnMount: true,
    }
  );
};

const useComment = (
  postId: string,
  cb?: Function
): IUseCommentHook<IPostEachCommentResponse> => {
  const [commentList, setCommentList] = useState<IPostEachCommentResponse[]>(
    []
  );
  const queryClient = useQueryClient();
  const infinityQueryReturn = useGetPostComments(postId);

  useEffect(() => {
    let clone = infinityQueryReturn.data as any;
    setCommentList((prev) =>
      !clone?.data
        ? prev
        : clone?.data?.length === 0
        ? clone?.data
        : [...prev, ...clone.data]
    );
  }, [infinityQueryReturn.data]);

  useEffect(() => {
    if (infinityQueryReturn.isLoading) {
      setCommentList([]);
    }
  }, [infinityQueryReturn.isLoading]);

  const updateComment: TCommentUpdate = useCallback(
    async (commentId: string, content: string) => {
      const newComment = await (putUpdatePostComment({
        commentId,
        postId,
        content,
      }) as unknown as Promise<IPostEachCommentResponse>);

      const commentIndexFound = commentList.findIndex(
        (comment) => comment.id === newComment.id
      );

      if (commentIndexFound === -1) {
        throw new Error('No reply found');
      }

      setCommentList((oldList) => {
        const deepCloneValue = deepClone(oldList);
        deepCloneValue.splice(commentIndexFound, 1, newComment);
        return deepCloneValue;
      });
    },
    [commentList, postId]
  );

  const addComment: TCommentAdd = useCallback(
    async (content: string, parentId?: string) => {
      const comment = await (postNewCommentToPost({
        postId,
        content: content,
        parentId,
      }) as unknown as Promise<IPostEachCommentResponse>);
      cb && cb(1);

      setCommentList((oldList) => {
        const deepCloneValue = deepClone(oldList);
        deepCloneValue.unshift(comment);
        return deepCloneValue;
      });
    },
    [cb, postId]
  );

  const deleteComment: TCommentDelete = useCallback(
    async (commentId: string) => {
      await deletePostComment(postId, commentId);

      const commentIndexFound = commentList.findIndex(
        (comment: any) => comment?.id === commentId
      );

      if (commentIndexFound === -1) {
        throw new Error('No reply found');
      }
      cb && cb(-1);

      setCommentList((oldList) => {
        const deepCloneValue = deepClone(oldList);
        deepCloneValue.splice(commentIndexFound, 1);
        return deepCloneValue;
      });
    },
    [cb, commentList, postId]
  );

  const voteComment: TCommentVote = useCallback(
    async (commentId: string, isVoted: boolean) => {
      await putVotePostComment(commentId);
      const commentIndexFound = commentList.findIndex(
        (comment: any) => comment?.id === commentId
      );
      if (commentIndexFound === -1) {
        throw new Error('No reply found');
      }
      setCommentList((oldList) => {
        const deepCloneValue = deepClone(oldList);

        deepCloneValue[commentIndexFound].isVoted = isVoted;
        if (isVoted) {
          deepCloneValue[commentIndexFound].votes += 1;
        } else {
          deepCloneValue[commentIndexFound].votes -= 1;
        }

        return deepCloneValue;
      });
    },
    [commentList]
  );

  const pinComment: TCommentPinTwoWays = useCallback(
    async (commentId: string) => {
      await putPostPinComment(postId, commentId);
      infinityQueryReturn.refetch();
      queryClient.resetQueries(RQPostCommentKey.PostPinnedComment);
      queryClient.resetQueries(RQPostCommentKey.PostCommentList);
      queryClient.resetQueries(RQPostCommentKey.PostSubComment);
    },
    [infinityQueryReturn, postId, queryClient]
  );

  return {
    commentList,
    updateComment,
    addComment,
    deleteComment,
    infinityQueryReturn,
    voteComment,
    pinComment,
  };
};

export default useComment;
